import { useState, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Share2, Copy, Check, ExternalLink, Download, Share } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface ShareButtonProps {
  url: string;
  title: string;
  description?: string;
  brandName?: string; // Brand name to show on QR card (e.g., "Học nhé" for V-Blog, or specific blog name for posts)
}

export const ShareButton = ({ url, title, description, brandName = 'Học nhé' }: ShareButtonProps) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const canShare = typeof navigator !== 'undefined' && !!navigator.share;
  const canShareFiles = typeof navigator !== 'undefined' &&
                        !!navigator.share &&
                        !!navigator.canShare;
  const qrCardRef = useRef<HTMLDivElement>(null);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Đã sao chép liên kết');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Không thể sao chép liên kết');
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title,
          text: description || title,
          url,
        });
      } catch (error: any) {
        // User cancelled share or error occurred
        if (error.name !== 'AbortError') {
          toast.error('Không thể chia sẻ');
        }
      }
    } else {
      toast.error('Trình duyệt không hỗ trợ chia sẻ');
    }
  };

  const handleDownloadQR = async () => {
    try {
      // Create a canvas from the QR code SVG
      const svg = qrCardRef.current?.querySelector('svg');
      if (!svg) return;

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Set canvas size (match the card design)
      const scale = 2; // Higher quality
      canvas.width = 300 * scale;
      canvas.height = 380 * scale;
      ctx.scale(scale, scale);

      // Draw white background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, 300, 380);

      // Draw brand header
      ctx.fillStyle = '#2563eb';
      ctx.fillRect(0, 0, 300, 80);

      // Draw logo/icon
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 32px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('✍️', 150, 40);

      // Draw brand name
      ctx.font = 'bold 18px sans-serif';
      ctx.fillText(brandName, 150, 65);

      // Draw title
      ctx.fillStyle = '#1f2937';
      ctx.font = 'bold 14px sans-serif';
      ctx.textAlign = 'center';
      const titleText = title.length > 40 ? title.substring(0, 37) + '...' : title;
      ctx.fillText(titleText, 150, 105);

      // Convert SVG to image and draw
      const svgData = new XMLSerializer().serializeToString(svg);
      const img = new Image();
      const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);

      img.onload = () => {
        // Draw QR code centered
        const qrSize = 200;
        ctx.drawImage(img, 50, 130, qrSize, qrSize);

        // Draw footer text
        ctx.fillStyle = '#6b7280';
        ctx.font = '12px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('Quét mã QR để truy cập', 150, 355);

        // Convert canvas to blob and download
        canvas.toBlob((blob) => {
          if (!blob) return;
          const downloadUrl = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = `qr-${title.replace(/[^a-z0-9]/gi, '-').toLowerCase()}.png`;
          link.click();
          URL.revokeObjectURL(downloadUrl);
          toast.success('Đã tải xuống mã QR');
        });

        URL.revokeObjectURL(url);
      };

      img.src = url;
    } catch (error) {
      console.error('Download QR error:', error);
      toast.error('Không thể tải xuống mã QR');
    }
  };

  const handleShareQR = async () => {
    // Check if sharing files is supported
    if (!navigator.share) {
      toast.error('Trình duyệt không hỗ trợ chia sẻ');
      return;
    }

    try {
      const svg = qrCardRef.current?.querySelector('svg');
      if (!svg) {
        toast.error('Không tìm thấy mã QR');
        return;
      }

      toast.info('Đang tạo ảnh QR...');

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const scale = 2;
      canvas.width = 300 * scale;
      canvas.height = 380 * scale;
      ctx.scale(scale, scale);

      // Draw white background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, 300, 380);

      // Draw brand header
      ctx.fillStyle = '#2563eb';
      ctx.fillRect(0, 0, 300, 80);

      // Draw logo
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 32px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('✍️', 150, 40);

      ctx.font = 'bold 18px sans-serif';
      ctx.fillText(brandName, 150, 65);

      // Draw title
      ctx.fillStyle = '#1f2937';
      ctx.font = 'bold 14px sans-serif';
      const titleText = title.length > 40 ? title.substring(0, 37) + '...' : title;
      ctx.fillText(titleText, 150, 105);

      // Convert SVG to image
      const svgData = new XMLSerializer().serializeToString(svg);
      const img = new Image();
      const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      const svgUrl = URL.createObjectURL(svgBlob);

      img.onload = async () => {
        // Draw QR code
        ctx.drawImage(img, 50, 130, 200, 200);

        // Draw footer
        ctx.fillStyle = '#6b7280';
        ctx.font = '12px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('Quét mã QR để truy cập', 150, 355);

        // Convert to blob
        canvas.toBlob(async (blob) => {
          if (!blob) {
            toast.error('Không thể tạo ảnh');
            return;
          }

          try {
            const fileName = `qr-hocnhe-${title.replace(/[^a-z0-9]/gi, '-').toLowerCase()}.png`;
            const file = new File([blob], fileName, { type: 'image/png' });

            // Check if can share files
            if (navigator.canShare && navigator.canShare({ files: [file] })) {
              await navigator.share({
                files: [file],
                title: `Mã QR - ${title}`,
                text: `Quét mã QR để truy cập: ${title}`,
              });
              toast.success('Đã chia sẻ mã QR');
            } else {
              // Browser doesn't support file sharing, download instead
              toast.warning('Trình duyệt không hỗ trợ chia sẻ ảnh, tải xuống thay thế');
              const downloadUrl = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = downloadUrl;
              link.download = fileName;
              link.click();
              URL.revokeObjectURL(downloadUrl);
            }
          } catch (error: any) {
            if (error.name === 'AbortError') {
              // User cancelled, do nothing
              console.log('Share cancelled by user');
            } else {
              console.error('Share error:', error);
              toast.error('Không thể chia sẻ: ' + (error.message || 'Lỗi không xác định'));
            }
          }
        }, 'image/png', 0.95);

        URL.revokeObjectURL(svgUrl);
      };

      img.onerror = () => {
        toast.error('Không thể tải mã QR');
        URL.revokeObjectURL(svgUrl);
      };

      img.src = svgUrl;
    } catch (error) {
      console.error('Share QR error:', error);
      toast.error('Lỗi khi chia sẻ mã QR');
    }
  };

  return (
    <>
      {/* Floating Share Button */}
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-4 sm:right-32 z-50 group"
        aria-label="Chia sẻ"
      >
        <div className="relative">
          {/* Gradient background with glow effect */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-lg group-hover:shadow-2xl transition-all" />
          <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-2xl blur-sm opacity-0 group-hover:opacity-50 transition-opacity" />

          {/* Button content */}
          <div className="relative px-3 py-2 sm:px-4 sm:py-3 flex items-center gap-1.5 sm:gap-2">
            <Share2 className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
            <span className="text-xs sm:text-sm font-medium text-white hidden xs:inline">Chia sẻ</span>
          </div>
        </div>
      </button>

      {/* Share Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-semibold">
              Chia sẻ
            </DialogTitle>
            <DialogDescription className="text-sm">
              Chia sẻ trang này với bạn bè của bạn
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 sm:space-y-6 py-2 sm:py-4">
            {/* QR Code Card with Branding */}
            <div className="flex flex-col items-center space-y-3">
              <div
                ref={qrCardRef}
                className="relative w-full max-w-[280px] bg-white rounded-xl border-2 border-gray-100 shadow-lg overflow-hidden"
              >
                {/* Brand Header */}
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-4 text-center">
                  <div className="text-3xl mb-1">✍️</div>
                  <div className="text-white font-bold text-base">{brandName}</div>
                </div>

                {/* Title */}
                <div className="px-4 py-3 bg-gray-50">
                  <h3 className="text-sm font-semibold text-gray-800 text-center line-clamp-2">
                    {title}
                  </h3>
                </div>

                {/* QR Code */}
                <div className="flex justify-center p-4 bg-white">
                  <QRCodeSVG
                    value={url}
                    size={180}
                    level="H"
                    includeMargin={false}
                  />
                </div>

                {/* Footer */}
                <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
                  <p className="text-xs text-gray-500 text-center">
                    Quét mã QR để truy cập
                  </p>
                </div>
              </div>

              {/* QR Actions */}
              <div className="flex gap-2 w-full max-w-[280px]">
                <Button
                  onClick={handleDownloadQR}
                  variant="outline"
                  size="sm"
                  className={canShareFiles ? "flex-1" : "w-full"}
                >
                  <Download className="h-4 w-4 mr-1.5" />
                  <span className="text-xs sm:text-sm">Tải xuống</span>
                </Button>
                {canShareFiles && (
                  <Button
                    onClick={handleShareQR}
                    variant="outline"
                    size="sm"
                    className="flex-1"
                  >
                    <Share className="h-4 w-4 mr-1.5" />
                    <span className="text-xs sm:text-sm">Chia sẻ</span>
                  </Button>
                )}
              </div>
            </div>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-white px-2 text-gray-500">hoặc</span>
              </div>
            </div>

            {/* Copy Link Section */}
            <div className="space-y-2">
              <label className="text-xs sm:text-sm font-medium text-gray-700">
                Liên kết
              </label>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <input
                  type="text"
                  value={url}
                  readOnly
                  className="flex-1 px-3 py-2 text-xs sm:text-sm border border-gray-300 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 truncate"
                />
                <Button
                  onClick={handleCopyLink}
                  variant={copied ? 'default' : 'outline'}
                  size="sm"
                  className="w-full sm:w-auto sm:min-w-[100px]"
                >
                  {copied ? (
                    <>
                      <Check className="h-4 w-4 mr-1" />
                      <span className="text-xs sm:text-sm">Đã sao chép</span>
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4 mr-1" />
                      <span className="text-xs sm:text-sm">Sao chép</span>
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Native Share Button */}
            {canShare && (
              <Button
                onClick={handleNativeShare}
                variant="outline"
                className="w-full"
                size="default"
              >
                <ExternalLink className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                <span className="text-xs sm:text-sm">Chia sẻ lên nền tảng khác</span>
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
